import React, { useCallback, useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { formatCurrency } from '../utils/helpers';
import toast from 'react-hot-toast';
import { loadCashfree, savePendingPayment, clearPendingPayment, verifyCashfreePayment } from '../utils/cashfree';
import { Wallet, Truck, Tag, CheckCircle,Ruler, XCircle, ArrowLeft, ShoppingBag, Gift, AlertCircle, Sparkles, MapPin, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { useSeo } from '../utils/seo';
import { detectAddress } from '../utils/geolocation';


const Checkout: React.FC = () => {
  useSeo({ title: 'Checkout', noindex: true });
  const { items, total, clearCart, updatePrices } = useCart();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'cod'>('online');
  // COD availability and charge are controlled from the admin Settings tab (off by default)
  const [codSettings, setCodSettings] = useState<{ cod_enabled: boolean; cod_charge: number }>({ cod_enabled: false, cod_charge: 49 });
  useEffect(() => {
    apiFetch('/api/settings?key=payment')
      .then((res: any) => { if (res?.value) setCodSettings(res.value); })
      .catch(() => { /* keep COD hidden if settings can't be loaded */ });
  }, []);
  useEffect(() => {
    if (!codSettings.cod_enabled && paymentMethod === 'cod') setPaymentMethod('online');
  }, [codSettings.cod_enabled, paymentMethod]);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Use the field names that worked (v1)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    alt_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    special_requests: '',
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [locationStatus, setLocationStatus] = useState<'idle' | 'detecting' | 'filled' | 'error'>('idle');
  const [locationMessage, setLocationMessage] = useState('');

  /* ---------------- LOCATION AUTOFILL ---------------- */
  // Fills address/city/state/pincode from the visitor's location.
  // Only empty fields are filled so we never overwrite what the user typed.
  const fillFromLocation = useCallback(async () => {
    setLocationStatus('detecting');
    setLocationMessage('');
    try {
      const detected = await detectAddress();
      if (!detected.city && !detected.state && !detected.pincode) {
        throw new Error('Could not find an address for your location. Please enter it manually.');
      }
      setFormData(prev => ({
        ...prev,
        address: prev.address.trim() ? prev.address : detected.address,
        city: prev.city.trim() ? prev.city : detected.city,
        state: prev.state.trim() ? prev.state : detected.state,
        pincode: prev.pincode.trim() ? prev.pincode : detected.pincode,
      }));
      setFieldErrors(prev => ({ ...prev, address: '', city: '', state: '', pincode: '' }));
      setLocationStatus('filled');
      setLocationMessage(
        detected.pincode
          ? 'Filled from your location. Please verify and add your house number.'
          : 'Filled from your location, but pincode was not found. Please enter it.'
      );
    } catch (e: any) {
      setLocationStatus('error');
      setLocationMessage(e.message || 'Could not detect your location.');
    }
  }, []);

  useEffect(() => {
    if (items.length) fillFromLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- CALCULATIONS ---------------- */
  const subtotal = total;
  const FREE_SHIPPING_THRESHOLD = 499; // Make sure this matches your requirement
  
  // FIX: Ensure shipping charge is calculated correctly
  const shippingCharge = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : 79;
  const codCharge = paymentMethod === 'cod' && codSettings.cod_enabled ? codSettings.cod_charge : 0;
  const grandTotal = Math.max(
    subtotal + shippingCharge + codCharge - couponDiscount,
    0
  );

  const amountToFreeShipping = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);
  const shippingProgress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

  /* ---------------- HELPERS ---------------- */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name?.trim()) {
      errors.name = "Please enter your full name";
    }
    if (!formData.email?.trim()) {
      errors.email = "Please enter your email";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.phone?.trim()) {
      errors.phone = "Please enter your phone number";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = "Please enter a valid 10-digit phone number";
    }
    const altPhone = formData.alt_phone.replace(/\D/g, '');
    if (altPhone) {
      if (!/^\d{10}$/.test(altPhone)) {
        errors.alt_phone = "Please enter a valid 10-digit phone number";
      } else if (altPhone === formData.phone.replace(/\D/g, '')) {
        errors.alt_phone = "Alternative number must be different from your phone number";
      }
    }
    if (!formData.address?.trim()) {
      errors.address = "Please enter your address";
    }
    if (!formData.city?.trim()) {
      errors.city = "Please enter your city";
    }
    if (!formData.state?.trim()) {
      errors.state = "Please enter your state";
    }
    if (!formData.pincode?.trim()) {
      errors.pincode = "Please enter your pincode";
    } else if (!/^\d{6}$/.test(formData.pincode.replace(/\D/g, ''))) {
      errors.pincode = "Please enter a valid 6-digit pincode";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Build complete address
  const getFullAddress = () => {
    return `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
  };

  /* ---------------- COUPON ---------------- */
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    // Block coupons if ALL items are combos
    const hasOnlyComboItems = items.length > 0 && items.every(item => item.type === 'combo');
    if (hasOnlyComboItems) {
      setCouponError('Coupons cannot be applied to combo orders');
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    setCouponSuccess('');

    try {
      // Collect all category names from non-combo cart items
      const cartCategories: string[] = [];
      items.forEach(item => {
        if (item.type === 'combo') return; // skip combos
        if ((item as any).categories) {
          (item as any).categories.forEach((c: any) => {
            if (c.name && !cartCategories.includes(c.name)) cartCategories.push(c.name);
          });
        } else if ((item as any).category && !cartCategories.includes((item as any).category)) {
          cartCategories.push((item as any).category);
        }
      });

      // Subtotal of non-combo items only for coupon validation
      const nonComboSubtotal = items
        .filter(item => item.type !== 'combo')
        .reduce((sum, item) => sum + item.price * item.quantity, 0);

      const data = await apiFetch('/api/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({
          code: couponCode,
          subtotal: nonComboSubtotal,
          email: formData.email || undefined,
          categories: cartCategories,
          hasOnlyComboItems,
        }),
      });

      if (data.valid) {
        setAppliedCoupon(data.coupon);
        setCouponDiscount(data.coupon.discount_amount);
        setCouponSuccess(`✨ Coupon applied! You saved ₹${data.coupon.discount_amount}`);
      } else {
        throw new Error(data.message || 'Invalid coupon');
      }
    } catch (e: any) {
      setCouponError(e.message || 'Error validating coupon');
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode('');
    setCouponError('');
    setCouponSuccess('');
  };

  /* ---------------- EXTRACT ORDER ID ---------------- */
  const extractOrderId = (response: any): string | null => {
    if (!response) return null;
    
    console.log('Extracting order ID from:', response);
    
    // Your response structure: { success: true, message: "...", order: { id: "..." } }
    if (response.order?.id) {
      return response.order.id;
    }
    
    // Try other common patterns
    const patterns = [
      response.order_id,
      response.orderId,
      response.id,
      response.data?.order_id,
      response.data?.id,
    ];
    
    for (const pattern of patterns) {
      if (pattern && typeof pattern === 'string') {
        return pattern;
      }
    }
    
    return null;
  };

  /* ---------------- COD ---------------- */
  const handleCODOrder = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const fullAddress = getFullAddress();
      
      // Use the v1 format that worked
      const requestBody = {
        items,
        subtotal,
        shipping_charge: shippingCharge,
        cod_charge: codCharge,
        coupon_code: appliedCoupon?.code || null,
        coupon_discount: couponDiscount,
        total_amount: grandTotal,
        payment_method: 'cod',
        // These field names worked
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        alt_phone: formData.alt_phone,
        address: fullAddress,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        special_requests: formData.special_requests || '',
      };

      console.log('📦 Sending COD request:', requestBody);

      const response = await apiFetch('/api/orders/cod', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      console.log('✅ COD Response:', response);

      // Extract order ID from response
      const orderId = extractOrderId(response);
      
      if (orderId) {
        console.log('🎉 Order placed! Order ID:', orderId);
        clearCart();
        // Redirect to confirmation page
        navigate(`/order-confirmation?orderId=${orderId}`);
      } else {
        console.warn('⚠️ No order ID in response, but order was placed');
        alert('Order placed successfully!');
        clearCart();
        navigate('/');
      }
      
    } catch (error: any) {
      console.error('❌ COD Error:', error);
      if (!handlePricingError(error)) toast.error(error?.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * The server prices every order itself. It answers PRICE_CHANGED (cart updated to the new
   * prices), COUPON_INVALID (coupon removed) or ITEM_UNAVAILABLE. Returns true when handled.
   */
  const handlePricingError = (error: any): boolean => {
    const code = error?.data?.code;
    if (code === 'PRICE_CHANGED') {
      if (Array.isArray(error.data.items)) updatePrices(error.data.items);
      toast.error(error.message, { duration: 7000 });
      return true;
    }
    if (code === 'COUPON_INVALID') {
      removeCoupon();
      toast.error(error.message, { duration: 6000 });
      return true;
    }
    if (code === 'ITEM_UNAVAILABLE') {
      toast.error(error.message, { duration: 7000 });
      return true;
    }
    return false;
  };

  /* ---------------- CASHFREE ---------------- */
  const handleOnlinePayment = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      if (!(await loadCashfree())) {
        throw new Error('Could not load the payment page. Please check your connection and try again.');
      }

      const orderDataPayload = {
        items,
        subtotal,
        shipping_charge: shippingCharge,
        coupon_code: appliedCoupon?.code,
        coupon_discount: couponDiscount,
        total_amount: grandTotal,
        payment_method: 'online',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        alt_phone: formData.alt_phone,
        address: getFullAddress(),
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        special_requests: formData.special_requests,
      };

      // 1. Backend creates the Cashfree order and returns a payment session
      const session = await apiFetch('/api/payment/cashfree/create-order', {
        method: 'POST',
        body: JSON.stringify({ order_data: orderDataPayload }),
      });
      if (!session?.payment_session_id) throw new Error(session?.error || 'Could not start the payment.');

      // Kept until the order is saved, in case the bank/UPI app redirects back to the site
      savePendingPayment({ order_id: session.order_id, order_data: orderDataPayload, created_at: Date.now() });

      // 2. Cashfree's secure checkout popup
      const cashfree = window.Cashfree!({ mode: session.mode === 'production' ? 'production' : 'sandbox' });
      const result = await cashfree.checkout({ paymentSessionId: session.payment_session_id, redirectTarget: '_modal' });

      if (result?.redirect) return; // a redirect flow took over; /order-confirmation finishes the order
      if (result?.error) {
        // closed the popup or the payment failed — nothing was charged
        toast.error(result.error.message || 'Payment was not completed.');
        return;
      }

      // 3. Backend confirms with Cashfree that it is paid, then saves the order
      const verified = await verifyCashfreePayment(session.order_id, orderDataPayload);
      if (verified.success && verified.order?.id) {
        clearPendingPayment();
        clearCart();
        navigate(`/order-confirmation?orderId=${verified.order.id}`);
      } else {
        toast.error(verified.error || 'We could not confirm your payment yet. If money was deducted, contact us with your phone number.');
      }
    } catch (e: any) {
      console.error('Payment error:', e);
      if (!handlePricingError(e)) toast.error(e.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (paymentMethod === 'online') {
      await handleOnlinePayment();
    } else {
      await handleCODOrder();
    }
  };

  /* ---------------- RENDER FORM FIELDS ---------------- */
  const renderField = (
    label: string,
    name: string,
    type: string = 'text',
    placeholder: string = '',
    required: boolean = true
  ) => (
    <div>
      <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          name={name}
          value={(formData as any)[name] || ''}
          onChange={handleInputChange}
          rows={2}
          className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none transition-colors ${
            fieldErrors[name] ? 'border-red-300 bg-red-50' : ''
          }`}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={(formData as any)[name] || ''}
          onChange={handleInputChange}
          maxLength={type === 'tel' ? 10 : undefined}
          className={`w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none transition-colors ${
            fieldErrors[name] ? 'border-red-300 bg-red-50' : ''
          }`}
          placeholder={placeholder}
        />
      )}
      {fieldErrors[name] && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {fieldErrors[name]}
        </p>
      )}
    </div>
  );

  /* ---------------- EMPTY CART ---------------- */
  if (!items.length) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-20 text-center">
        <div className="max-w-md mx-auto">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-premium-cream rounded-full mb-6">
            <ShoppingBag className="h-12 w-12 text-premium-gold" />
          </div>
          <h1 className="text-3xl font-serif font-bold mb-4">Your cart is empty</h1>
          <p className="text-gray-600 mb-8">Add some beautiful gifts to make someone's day special</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-premium-gold text-white rounded-lg hover:bg-premium-burgundy transition-colors"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 md:py-12">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center text-gray-600 mb-4 md:hidden hover:text-premium-gold transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          Back to Cart
        </button>

        <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold mb-6 md:mb-8 text-center">
          Secure <span className="text-premium-gold">Checkout</span>
        </h1>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
          {/* Order Summary */}
          <div className="order-1 lg:order-1">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 sticky top-20">
              <h2 className="text-xl md:text-2xl font-serif font-semibold mb-4 md:mb-6">Order Summary</h2>
              
              {/* Free Shipping Progress Bar */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-700">Free Shipping</span>
                  </div>
                  {shippingCharge === 0 ? (
                    <span className="text-green-600 font-semibold flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      You're eligible! 🎉
                    </span>
                  ) : (
                    <span className="text-blue-600 font-medium">
                      Add ₹{amountToFreeShipping.toLocaleString()} more
                    </span>
                  )}
                </div>
                
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${shippingProgress}%` }}
                  />
                </div>
                
                {shippingCharge > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">
                      Just ₹{amountToFreeShipping} more for FREE shipping!
                    </p>
                    <button
                      onClick={() => navigate('/products')}
                      className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Shop ₹{amountToFreeShipping.toLocaleString()} more
                    </button>
                  </div>
                )}
              </div>
              
              {/* Items List with Color and Size Info */}
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6 max-h-[300px] md:max-h-96 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {item.color_code && (
                          <div className="flex items-center gap-1">
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300"
                              style={{ backgroundColor: item.color_code }}
                              title={item.color_name}
                            />
                            {item.color_name && (
                              <span className="text-xs text-gray-600">{item.color_name}</span>
                            )}
                          </div>
                        )}
                        {item.size_name && (
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded flex items-center gap-1">
                            <Ruler className="h-3 w-3" />
                            Size: {item.size_name}
                            {item.size_code && ` (${item.size_code})`}
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-sm md:text-base line-clamp-1">{item.name}</h4>
                      <p className="text-xs md:text-sm text-gray-600">
                        Qty: {item.quantity} × {formatCurrency(item.price)}
                      </p>
                    </div>
                    <p className="font-semibold text-sm md:text-base whitespace-nowrap">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-2 md:space-y-3 pt-3 md:pt-4 border-t">
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-600">Subtotal:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-600">Shipping:</span>
                  <span className={shippingCharge === 0 ? 'text-green-600 font-medium' : ''}>
                    {shippingCharge === 0 ? (
                      <span className="flex items-center gap-1">
                        <Gift className="h-4 w-4" />
                        FREE
                      </span>
                    ) : formatCurrency(shippingCharge)}
                  </span>
                </div>
                {paymentMethod === 'cod' && (
                  <div className="flex justify-between text-sm md:text-base">
                    <span className="text-gray-600">COD Charges:</span>
                    <span>{formatCurrency(codCharge)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm md:text-base text-green-600">
                    <span>Coupon Discount:</span>
                    <span>-{formatCurrency(couponDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base md:text-xl font-bold pt-3 border-t">
                  <span>Total:</span>
                  <span className="text-premium-gold">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details Form */}
          <div className="order-2 lg:order-2">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6">
              <h2 className="text-xl md:text-2xl font-serif font-semibold mb-4 md:mb-6">Shipping Details</h2>

              {/* Location Autofill */}
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-premium-cream/50 border border-premium-gold/20 rounded-lg">
                <p className={`text-xs md:text-sm flex items-center gap-2 ${
                  locationStatus === 'error' ? 'text-red-600' : locationStatus === 'filled' ? 'text-green-700' : 'text-gray-600'
                }`}>
                  {locationStatus === 'detecting' ? (
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                  ) : (
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                  )}
                  {locationStatus === 'detecting'
                    ? 'Detecting your location...'
                    : locationMessage || 'Autofill your address using your current location'}
                </p>
                <button
                  type="button"
                  onClick={fillFromLocation}
                  disabled={locationStatus === 'detecting'}
                  className="text-xs md:text-sm font-medium text-premium-gold hover:text-premium-burgundy disabled:opacity-50 whitespace-nowrap"
                >
                  Use my current location
                </button>
              </div>

              <div className="space-y-3 md:space-y-4">
                {renderField('Full Name', 'name', 'text', 'Enter your full name')}
                {renderField('Email', 'email', 'email', 'Enter your email')}
                {renderField('Phone Number', 'phone', 'tel', '10-digit mobile number')}
                {renderField('Alternative Phone Number', 'alt_phone', 'tel', 'Optional, in case we cannot reach you', false)}
                {renderField('Address', 'address', 'textarea', 'House no, Street, Area')}

                {/* City, State, Pincode */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none ${
                        fieldErrors.city ? 'border-red-300 bg-red-50' : ''
                      }`}
                      placeholder="City"
                    />
                    {fieldErrors.city && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.city}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2">State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none ${
                        fieldErrors.state ? 'border-red-300 bg-red-50' : ''
                      }`}
                      placeholder="State"
                    />
                    {fieldErrors.state && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.state}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2">Pincode *</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      maxLength={6}
                      className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none ${
                        fieldErrors.pincode ? 'border-red-300 bg-red-50' : ''
                      }`}
                      placeholder="6-digit pincode"
                    />
                    {fieldErrors.pincode && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>
                    )}
                  </div>
                </div>

                {/* Coupon Section */}
                <div className="border-t pt-3 md:pt-4 mt-3 md:mt-4">
                  <h3 className="font-medium mb-2 md:mb-3 flex items-center text-sm md:text-base">
                    <Tag className="h-4 w-4 mr-2" />
                    Got a coupon? 🏷️
                  </h3>
                  
                  {appliedCoupon ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-green-800 text-sm md:text-base truncate">{appliedCoupon.code}</p>
                            <p className="text-xs md:text-sm text-green-600">
                              {appliedCoupon.discount_type === 'percentage' 
                                ? `${appliedCoupon.discount_value}% off`
                                : `₹${appliedCoupon.discount_value} off`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                      {couponSuccess && (
                        <p className="text-green-600 text-xs mt-2 flex items-center gap-1">
                          <Sparkles className="h-3 w-3" />
                          {couponSuccess}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {items.every(item => item.type === 'combo') ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                          🚫 Coupons are not applicable on combo orders
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Enter coupon code"
                              className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none"
                            />
                            <button
                              onClick={validateCoupon}
                              disabled={couponLoading}
                              className="px-4 py-2 bg-premium-gold text-white rounded-lg hover:bg-premium-burgundy disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                            >
                              {couponLoading ? '...' : 'Apply'}
                            </button>
                          </div>
                          {couponError && (
                            <p className="text-red-600 text-xs mt-2">{couponError}</p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Payment Method */}
                <div className="border-t pt-3 md:pt-4 mt-3 md:mt-4">
                  <h3 className="font-medium mb-2 md:mb-3 flex items-center text-sm md:text-base">
                    <Wallet className="h-4 w-4 mr-2" />
                    Payment Method
                  </h3>
                  
                  <div className={`grid grid-cols-1 gap-3 ${codSettings.cod_enabled ? 'sm:grid-cols-2' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('online')}
                      className={`p-3 border rounded-lg text-left transition-all ${
                        paymentMethod === 'online'
                          ? 'border-premium-gold bg-premium-gold/5 ring-2 ring-premium-gold/20'
                          : 'border-gray-200 hover:border-premium-gold'
                      }`}
                    >
                      <p className="font-medium text-sm md:text-base">Pay Online</p>
                      <p className="text-xs text-gray-600">Cards, UPI, NetBanking</p>
                    </button>

                    {codSettings.cod_enabled && (
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('cod')}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          paymentMethod === 'cod'
                            ? 'border-premium-gold bg-premium-gold/5 ring-2 ring-premium-gold/20'
                            : 'border-gray-200 hover:border-premium-gold'
                        }`}
                      >
                        <p className="font-medium text-sm md:text-base">Cash on Delivery</p>
                        <p className="text-xs text-gray-600">
                          {codSettings.cod_charge > 0 ? `${formatCurrency(codSettings.cod_charge)} COD fee · pay at delivery` : 'Pay at delivery'}
                        </p>
                      </button>
                    )}
                  </div>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2">Special Requests</label>
                  <textarea
                    name="special_requests"
                    value={formData.special_requests}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:border-premium-gold focus:outline-none"
                    placeholder="Gift wrapping, delivery instructions, etc."
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className={`w-full py-3 md:py-4 rounded-lg font-medium text-base md:text-lg transition-all mt-4 ${
                    loading 
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-premium-gold to-premium-burgundy text-white hover:scale-[1.02] shadow-lg'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Processing...
                    </div>
                  ) : (
                    `Place Order • ${formatCurrency(grandTotal)}`
                  )}
                </button>

                {/* Shipping Info */}
                <div className="space-y-2 mt-4 text-xs md:text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-green-600" />
                    <span>Free shipping on orders above ₹{FREE_SHIPPING_THRESHOLD}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    By placing this order, you agree to our terms and conditions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
